import { ChevronDownIcon } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'
import { Button } from '../Button'
import { OverlayContent } from '../OverlayContent'
import clsx, { ClassValue } from 'clsx'
import { Menu } from '../Menu'

export type CustomSelectProps = {
  options: { label: ReactNode, value?: string, disabled?: boolean }[]
  value?: string | number
  showOptionValues?: boolean
  label?: ReactNode
  disabled?: boolean
  error?: boolean
  onChange?: (value?: string) => void
  buttonClassName?: ClassValue
}

export function CustomSelect (props: CustomSelectProps) {
  const { options, showOptionValues, label, disabled, error, buttonClassName, value, onChange } = props
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [valueSelected, setValueSelected] = useState(value)
  const [menuWidth, setMenuWidth] = useState(0)

  const option = options.find(options => options.value === value)

  const handleClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    if (anchorEl)setMenuWidth(anchorEl.getBoundingClientRect().width)
  }, [anchorEl])

  const handleOptionClick = (value?: string) => {
    setValueSelected(value)
    onChange?.(value)
    handleClose()
  }

  return (
    <>
      <label className="fieldset">
        {label && (
          <legend>
            <span className='text-sm text-gray-500'>
              {label}
            </span>
          </legend>
        )}
        <Button
          className={clsx(
            'w-full flex no-animation bg-white shadow-none rounded-lg text-sm py-[14px] -mt-1 pl-[9px] -mb-1',
            error && 'border-error border-1',
            buttonClassName
          )}
          size="xs"
          disabled={disabled}
          onClick={event => setAnchorEl(event.currentTarget)}
        >
          <span className="text-left font-normal grow truncate max-w-[calc(100%-32px)]">{option?.label}</span>
          <ChevronDownIcon size={20} />
        </Button>
      </label>

      <OverlayContent
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Menu
          className="overflow-y-auto w-full max-h-[500px]"
          style={{ scrollbarWidth: 'thin', width: `${menuWidth}px` }}
          maxWidth={false}
        >
          {options.map(option => (
            <li
              key={option.value}
              className={clsx(option.disabled && 'menu-disabled')}
            >
              <a
                onClick={() => handleOptionClick(option.value)}
                className={clsx(
                  'flex flex-col items-start gap-1',
                  option.value === valueSelected && 'bg-base-200'
                )}
              >
                <span>{option.label}</span>
                {showOptionValues && <code className="text-xs font-semibold opacity-30">{option.value}</code>}
              </a>
            </li>
          ))}
        </Menu>
      </OverlayContent>
    </>
  )
}
